import { useEffect, useState } from 'react';
import { Alert, Spin, Space, Card, Statistic, Row, Col } from 'antd';
import axios from 'axios';

/**
 * Execute step.
 *
 * Takes the tuples validated by the prior step and POSTs them to /import/execute.
 * Calls onResult(true) on success, onResult(false) on failure.
 *
 * Reached only after Verify has passed clean, so happy-path is expected.
 */
const ImportExecution = ({ tuples, onResult }) => {
    const [state, setState] = useState({ status: 'idle', summary: null, error: null });

    useEffect(() => {
        if (!tuples || !tuples.length) {
            setState({ status: 'error', summary: null, error: 'No data to import.' });
            onResult(false);
            return;
        }

        let cancelled = false;
        setState({ status: 'loading', summary: null, error: null });

        axios
            .post('/import/execute', { tuples })
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success) {
                    setState({ status: 'done', summary: res.data.summary, error: null });
                    onResult(true);
                } else {
                    setState({
                        status: 'error',
                        summary: null,
                        error: res.data?.error || 'Import failed.',
                    });
                    onResult(false);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setState({
                    status: 'error',
                    summary: null,
                    error: err?.response?.data?.error || err.message || 'Import failed.',
                });
                onResult(false);
            });

        return () => {
            cancelled = true;
        };
    }, [tuples, onResult]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {state.status === 'loading' ? (
                <Alert
                    type="info"
                    message={<Spin size="small" />}
                    description="Importing values. This may take a moment for large sheets…"
                />
            ) : null}

            {state.status === 'error' ? (
                <Alert type="error" showIcon message="Import failed" description={state.error} />
            ) : null}

            {state.status === 'done' && state.summary ? (
                <>
                    <Alert type="success" showIcon message="Import complete" />
                    <Card size="small">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Statistic
                                    title="Values imported"
                                    value={state.summary.updatesApplied}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Scenarios touched"
                                    value={state.summary.scenariosTouched?.length ?? 0}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Skipped"
                                    value={state.summary.skipped?.length ?? 0}
                                />
                            </Col>
                        </Row>
                    </Card>
                </>
            ) : null}
        </Space>
    );
};

export default ImportExecution;
